import { ChangeEvent, FC, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';

import { Typography } from '@/utils/theme';

import { SrOnly } from '../Layout/Parts';
import Text from '../Text';

export const Option = styled.label`
  position: relative;
  padding: 12px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  transition: 0.2s ease-out background-color;
  cursor: pointer;

  ${Text} {
    flex: 1;
    padding-right: 15px;
    transition: 0.2s ease-out color;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    ${Text} {
      color: ${({ theme }) => theme.colors.black};
    }
  }
`;

export const Radio = styled.div<{ $active: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s ease-out border-color;
  margin-left: auto;
  flex-shrink: 0;
  > span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: transparent;
    transition: 0.2s ease-out background-color;
  }
  &:hover {
    border-color: ${({ theme }) => theme.colors.purple};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      border-color: ${theme.colors.purple};
      > span {
        background-color: ${theme.colors.purple};
      }
    `}
`;

interface Props {
  value: string | number;
  checked: boolean;
  setValue: (newValue: string) => void;
  id: string;
  textType?: Typography;
}

const FilterRadioButton: FC<PropsWithChildren<Props>> = ({
  value,
  checked,
  setValue,
  id,
  textType = 'smallBold',
  children,
}) => {
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  return (
    <Option htmlFor={id}>
      <Text as="span" $color="gray" $type={textType}>
        {children}
      </Text>
      <Radio $active={checked}>
        <span />
      </Radio>
      <SrOnly
        id={id}
        as="input"
        type="radio"
        value={value}
        checked={checked}
        onChange={onChange}
      />
    </Option>
  );
};

export default FilterRadioButton;
